import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { StepperModule } from 'primeng/stepper';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TableModule } from 'primeng/table';
import { FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { CoreApiService } from '../../../../core/service/core-api-service';
import { JsonRequest } from '../../../../model/jsonRequest.model';
import { properties } from '../../../../../environments/properties';
import { AppService } from '../../../../core/service/app.service';
import { StoreScheduleModel } from '../../../../model/store-schedule.model';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DialogModule } from 'primeng/dialog';
import { CentsCurrencyInputDirective } from '../../../../shared/directives/cents-currency-input.directive';
import { compressImage } from '../../../../shared/utils/image-compression.util';
import { isAllowedUploadType } from '../../../../shared/utils/file-type-validator.util';
import { StoreManagerCreate } from '../../../../model/store-manager.model';

interface UploadEvent {
  files: File[];
}

export const MANAGER_PASSWORD_MIN_LENGTH = 6;
// Texto exacto de la BadRequestException del backend (gestiontaxiserver spec 074).
// Si allá cambia, el error cae al toast genérico con el mensaje recibido.
export const MANAGER_USER_TAKEN = 'Store manager username already exists';
const MANAGER_STEP = 4;

const notBlank: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  control.value && !String(control.value).trim() ? { blank: true } : null;

const passwordsMatch: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const password = group.get('adm_password')?.value;
  const confirm = group.get('adm_password_confirm')?.value;
  return password && confirm && password !== confirm ? { mismatch: true } : null;
};

@Component({
  selector: 'app-create-store',
  standalone: true,
  imports: [
    StepperModule,
    ButtonModule,
    InputTextModule,
    ToggleButtonModule,
    CommonModule,
    FormsModule,
    FloatLabelModule,
    ReactiveFormsModule,
    TableModule,
    FileUploadModule,
    ImageModule,
    DropdownModule,
    CalendarModule,
    InputNumberModule,
    InputTextareaModule,
    GoogleMapsModule,
    DialogModule,
    CentsCurrencyInputDirective,
  ],
  templateUrl: './create-store.component.html',
  styleUrl: './create-store.component.css',
})
export class CreateStoreComponent implements OnInit, AfterViewInit {
  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  titulo: string = 'Crear Negocio';
  step1_titulo: string = 'Información Básica';
  step2_titulo: string = 'Horarios de Recepción de Pedidos';
  step3_titulo: string = 'Ubicación y Datos Comerciales';
  step4_titulo: string = 'Logo';
  step5_titulo: string = 'Usuario Gestor';
  step6_titulo: string = 'Resumen';
  readonly passwordMinLength = MANAGER_PASSWORD_MIN_LENGTH;
  showPassword: boolean = false;
  showPasswordConfirm: boolean = false;
  showCredentialsDialog: boolean = false;
  createdCredentials: { user: string; password: string } | null = null;
  active: number = 0;
  loading: boolean = false;
  showConfirmDialog: boolean = false;
  schedulesAttempted: boolean = false;
  ltsSchedules: StoreScheduleModel[] = [];
  ltsTypeDocument: any[] = [];
  ltsDocument: any[] = [];
  ltsPolDivisions: any[] = [];
  imageLogo: File | null = null;
  logoPreview: string | null = null;
  logoAttempted: boolean = false;

  mapCenter: google.maps.LatLngLiteral = { lat: -2.8978, lng: -79.0045 };
  mapZoom: number = 12;
  mapOptions: google.maps.MapOptions = { mapId: 'DEMO_MAP_ID' };
  private storeMarker: any = null;
  private mapInitialized: boolean = false;

  formStore = new FormGroup({
    sto_name: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    sto_description: new FormControl('', Validators.maxLength(500)),
    sto_phone: new FormControl('', [Validators.required, Validators.maxLength(20)]),
    sto_is_featured: new FormControl(false),
  });

  formLocation = new FormGroup({
    sto_address: new FormControl('', Validators.required),
    sto_political_division_id: new FormControl('', Validators.required),
    sto_delivery_radius_km: new FormControl(5, Validators.required),
    sto_min_order_amount: new FormControl(0, Validators.required),
    sto_delivery_fee: new FormControl(0, Validators.required),
    sto_lat: new FormControl<number | null>(null, Validators.required),
    sto_lng: new FormControl<number | null>(null, Validators.required),
  });

  formManager = new FormGroup(
    {
      adm_name: new FormControl('', [Validators.required, notBlank, Validators.maxLength(50)]),
      adm_last_name: new FormControl('', [Validators.required, notBlank, Validators.maxLength(50)]),
      adm_user: new FormControl('', [
        Validators.required,
        Validators.maxLength(50),
        // Sin espacios internos; los de los bordes se recortan al enviar.
        Validators.pattern(/^\s*\S+\s*$/),
      ]),
      adm_password: new FormControl('', [
        Validators.required,
        Validators.minLength(MANAGER_PASSWORD_MIN_LENGTH),
      ]),
      adm_password_confirm: new FormControl('', Validators.required),
    },
    { validators: passwordsMatch },
  );

  constructor(
    private readonly apiService: CoreApiService,
    private readonly util: AppService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.initializeSchedules();
    this.getPolDivisions();
  }

  ngAfterViewInit(): void {
    // no-op: autocomplete initialized in next() when user navigates to step 3
  }

  initializeSchedules(): void {
    this.ltsSchedules = [];
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    for (let i = 0; i < 7; i++) {
      this.ltsSchedules.push({
        ssh_id: 0,
        ssh_day_of_week: i,
        ssh_open_time: '09:00',
        ssh_close_time: '18:00',
        ssh_is_open: false,
        ssh_store_id: 0,
      });
    }
  }

  getPolDivisions(): void {
    const body: JsonRequest = {
      data: '',
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };
    this.loading = true;
    this.apiService.consumirWSGet(
      properties.servicePoliticalDivisions,
      body,
      (data) => {
        this.ltsPolDivisions = data.data;
        this.loading = false;
      },
    );
  }

  next(): void {
    if (this.active === 0 && this.formStore.invalid) {
      this.formStore.markAllAsTouched();
      return;
    }
    if (this.active === 1 && !this.hasActiveSchedule) {
      this.schedulesAttempted = true;
      return;
    }
    if (this.active === 2 && this.formLocation.invalid) {
      this.formLocation.markAllAsTouched();
      return;
    }
    if (this.active === 3 && !this.imageLogo) {
      this.logoAttempted = true;
      return;
    }
    if (this.active === MANAGER_STEP && this.formManager.invalid) {
      this.formManager.markAllAsTouched();
      return;
    }
    this.active++;
    if (this.active === 2 && !this.mapInitialized) {
      setTimeout(() => this.initAutocomplete(), 100);
    }
  }

  back(): void {
    this.active--;
  }

  backStart(): void {
    this.active = 0;
  }

  initAutocomplete(): void {
    const input = document.getElementById('storeAddressInput') as HTMLInputElement;
    if (!input) return;

    const autocomplete = new google.maps.places.Autocomplete(input, {
      fields: ['geometry', 'formatted_address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry?.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      this.formLocation.get('sto_address')?.setValue(place.formatted_address ?? '');
      this.formLocation.get('sto_lat')?.setValue(lat);
      this.formLocation.get('sto_lng')?.setValue(lng);
      this.mapCenter = { lat, lng };
      this.mapZoom = 17;

      this.placeStoreMarker({ lat, lng });
    });

    this.mapInitialized = true;
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    this.formLocation.get('sto_lat')?.setValue(lat);
    this.formLocation.get('sto_lng')?.setValue(lng);

    this.placeStoreMarker({ lat, lng });
  }

  async placeStoreMarker(position: google.maps.LatLngLiteral): Promise<void> {
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as any;
    const map = this.googleMap?.googleMap;
    if (!map) return;

    if (this.storeMarker) {
      this.storeMarker.map = null;
    }

    this.storeMarker = new AdvancedMarkerElement({
      map,
      position,
      gmpDraggable: true,
      title: 'Ubicación del negocio',
    });

    this.storeMarker.addListener('dragend', (event: any) => {
      this.formLocation.get('sto_lat')?.setValue(event.latLng.lat());
      this.formLocation.get('sto_lng')?.setValue(event.latLng.lng());
    });

    map.panTo(position);
  }

  async onUploadLogo(files: File[], fileUpload: any) {
    const doc = files[0];
    if (!isAllowedUploadType(doc, false)) {
      this.util.mostrarToast(
        'Crear Negocio',
        'Formato no permitido. Solo se aceptan imágenes PNG o JPG.',
        3,
      );
      fileUpload.clear();
      return;
    }
    const file = await compressImage(doc);
    this.imageLogo = file;
    this.logoPreview = await this.previewFile(file);
    this.logoAttempted = false;
    fileUpload.clear();
  }

  previewFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          reject('Error al leer el archivo');
        }
      };
      reader.onerror = () => {};
    });
  }

  removeFile(): void {
    this.imageLogo = null;
    this.logoPreview = null;
  }

  showConfirmation(): void {
    this.showConfirmDialog = true;
  }

  crearStore(): void {
    if (!this.imageLogo) {
      this.logoAttempted = true;
      this.util.mostrarToast(
        'Crear Negocio',
        'Debe cargar el logo del negocio',
        3,
      );
      return;
    }

    const storeData: any = {
      sto_name: this.formStore.get('sto_name')?.value ?? '',
      sto_description: this.formStore.get('sto_description')?.value ?? '',
      sto_phone: this.formStore.get('sto_phone')?.value ?? '',
      sto_is_featured: this.formStore.get('sto_is_featured')?.value ?? false,
      sto_address: this.formLocation.get('sto_address')?.value ?? '',
      sto_political_division_id:
        this.formLocation.get('sto_political_division_id')?.value ?? '',
      sto_delivery_radius_km:
        this.formLocation.get('sto_delivery_radius_km')?.value ?? 5,
      sto_min_order_amount:
        this.formLocation.get('sto_min_order_amount')?.value ?? 0,
      sto_delivery_fee: this.formLocation.get('sto_delivery_fee')?.value ?? 0,
      sto_lat: this.formLocation.get('sto_lat')?.value ?? null,
      sto_lng: this.formLocation.get('sto_lng')?.value ?? null,
      schedules: this.ltsSchedules
        .filter((s) => s.ssh_is_open)
        .map((schedule) => ({
          ...schedule,
          ssh_open_time: this.formatScheduleTime(schedule.ssh_open_time),
          ssh_close_time: this.formatScheduleTime(schedule.ssh_close_time),
        })),
      manager: this.buildManager(),
    };

    const formData = new FormData();
    formData.append('datos', JSON.stringify(storeData));
    formData.append('files', this.imageLogo, this.imageLogo.name);

    this.loading = true;
    this.apiService.consumirWSFilePost(
      properties.serviceCreateStore,
      formData,
      (data) => {
        this.loading = false;
        if (data.estado === properties.codeOK) {
          this.createdCredentials = {
            user: data.data?.manager?.adm_user ?? storeData.manager.adm_user,
            password: storeData.manager.adm_password,
          };
          this.showCredentialsDialog = true;
          this.util.mostrarToast(
            'Crear Negocio',
            'Se ha creado el negocio correctamente',
            1,
          );
        } else if (data.mensaje === MANAGER_USER_TAKEN) {
          this.active = MANAGER_STEP;
          const user = this.formManager.controls.adm_user;
          user.setErrors({ ...(user.errors ?? {}), taken: true });
          user.markAsTouched();
          this.util.mostrarToast(
            'Crear Negocio',
            'El usuario del gestor ya existe, elija otro',
            3,
          );
        } else {
          this.util.mostrarToast(
            'Crear Negocio',
            data.mensaje || 'Error al crear el negocio',
            3,
          );
        }
      },
    );
  }

  buildManager(): StoreManagerCreate {
    const value = this.formManager.getRawValue();
    return {
      adm_name: (value.adm_name ?? '').trim(),
      adm_last_name: (value.adm_last_name ?? '').trim(),
      adm_user: (value.adm_user ?? '').trim(),
      // La contraseña se envía tal cual: el backend la guarda sin recortar.
      adm_password: value.adm_password ?? '',
    };
  }

  copyCredentials(): void {
    if (!this.createdCredentials) return;
    const text = `Usuario: ${this.createdCredentials.user}\nContraseña: ${this.createdCredentials.password}`;
    navigator.clipboard?.writeText(text).then(
      () => this.util.mostrarToast('Crear Negocio', 'Credenciales copiadas al portapapeles', 1),
      () => this.util.mostrarToast('Crear Negocio', 'No se pudieron copiar las credenciales', 3),
    );
  }

  closeCredentialsDialog(): void {
    this.showCredentialsDialog = false;
    this.createdCredentials = null;
    this.formStore.reset();
    this.formLocation.reset();
    this.formManager.reset();
    this.ltsSchedules = [];
    this.imageLogo = null;
    this.logoPreview = null;
    this.router.navigate(['/admin/store']);
  }

  getDayName(dayOfWeek: number): string {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[dayOfWeek];
  }

  formatScheduleTime(time: string | Date): string {
    if (typeof time === 'string') {
      return time;
    }

    return `${time.getHours().toString().padStart(2, '0')}:${time
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  }

  getPolDivisionName(): string {
    const pdCode = this.formLocation.get('sto_political_division_id')?.value;
    const division = this.ltsPolDivisions.find((x) => x.pd_code === pdCode);
    return division?.pd_name ?? '';
  }

  getActiveSchedules(): StoreScheduleModel[] {
    return this.ltsSchedules.filter((x) => x.ssh_is_open);
  }

  get hasActiveSchedule(): boolean {
    return this.ltsSchedules.some((x) => x.ssh_is_open);
  }

  confirmAndCreate(): void {
    this.crearStore();
    this.showConfirmDialog = false;
  }
}
